import type {App, UserEvent} from '@cohort/merchants/apps';
import {getUserEvents} from '@cohort/merchants/apps';
import {useApps} from '@cohort/merchants/apps/useApps';
import type {AppStruct} from '@cohort/shared/apps/app';
import type {UserEventStruct} from '@cohort/shared/apps/userEvent';
import {useTranslation} from 'react-i18next';

export type Output<UE extends UserEventStruct, A extends AppStruct> = {
  getUserEvent: (userEventId: UE['Id']) => UserEvent<UE>;
  getUserEventTitle: (userEventId: UE['Id']) => string;
  getUserEventApp: (userEventId: UE['Id']) => App<A>;
};

export const useUserEventApps = <UE extends UserEventStruct, A extends AppStruct>(): Output<
  UE,
  A
> => {
  const {t} = useTranslation();
  const apps = useApps();

  const getUserEvent = (userEventId: UE['Id']): UserEvent<UE> => {
    const userEvent = getUserEvents().find(integration => integration.spec.id === userEventId);
    if (!userEvent) {
      throw new Error(`No UserEvent found for userEventId: ${userEventId}`);
    }
    return userEvent as unknown as UserEvent<UE>;
  };

  const getUserEventTitle = (userEventId: UE['Id']): string => {
    const userEvent = getUserEvent(userEventId);
    const [appId, eventId, action] = userEvent.spec.id.split('.');
    return t(`userEvents.${eventId}.${action}.title`, {
      ns: `app-${appId}`,
    });
  };

  const getUserEventApp = (userEventId: UE['Id']): App<A> => {
    const userEvent = getUserEvent(userEventId);
    const [appId] = userEvent.spec.id.split('.');
    const app = apps.getApp(appId);
    if (!app) {
      throw new Error(`No App found for userEventId: ${userEventId}`);
    }
    return app as App<A>;
  };

  return {
    getUserEvent,
    getUserEventTitle,
    getUserEventApp,
  };
};
